import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ShoppingCart,
  CreditCard,
  Check,
  X,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ExternalLink,
  Copy,
  Send,
  Link2,
  Loader2,
  Lock,
  Unlock,
  UserPlus,
  Mail,
  User,
  Calendar,
  DollarSign,
  Shield,
  Package,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

// ============ STATUS BADGES ============
const WhopStatusBadge = ({ status }) => {
  const config = {
    active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2, label: 'Active' },
    trialing: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Clock, label: 'Trialing' },
    past_due: { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: AlertTriangle, label: 'Past Due' },
    canceled: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', icon: XCircle, label: 'Canceled' },
    expired: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', icon: XCircle, label: 'Expired' },
    paused: { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock, label: 'Paused' },
    invalid: { bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertCircle, label: 'Invalid' },
  };
  const c = config[status] || config.invalid;
  const Icon = c.icon;
  return (
    <Badge className={`${c.bg} ${c.text} border-0 text-xs flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </Badge>
  );
};

const SelfRepairAccessBadge = ({ access }) => {
  const config = {
    active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: Unlock, label: 'Active' },
    pending: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Clock, label: 'Pending Signup' },
    locked: { bg: 'bg-red-500/20', text: 'text-red-400', icon: Lock, label: 'Locked' },
  };
  const c = config[access] || config.locked;
  const Icon = c.icon;
  return (
    <Badge className={`${c.bg} ${c.text} border-0 text-xs flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </Badge>
  );
};

const InviteStatusBadge = ({ status }) => {
  const config = {
    pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Pending' },
    accepted: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Accepted' },
    expired: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', label: 'Expired' },
    revoked: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Revoked' },
  };
  const c = config[status] || config.pending;
  return <Badge className={`${c.bg} ${c.text} border-0 text-xs`}>{c.label}</Badge>;
};

// ============ SEND CHECKOUT LINK MODAL ============
const SendCheckoutLinkModal = ({ isOpen, onClose, contactId, contactEmail, agencyId, token, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [selectedMapping, setSelectedMapping] = useState('');
  const [generatedLink, setGeneratedLink] = useState(null);

  useEffect(() => {
    if (isOpen && token) {
      fetchMappings();
      setGeneratedLink(null);
      setSelectedMapping('');
    }
  }, [isOpen, token]);

  const fetchMappings = async () => {
    try {
      const res = await axios.get(`${API}/api/platform/whop/plan-mappings-v2`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMappings((res.data.items || []).filter(m => m.status === 'active' && m.self_repair_enabled));
    } catch (err) {
      console.error('Failed to fetch mappings:', err);
    }
  };

  const handleGenerate = async () => {
    if (!selectedMapping) {
      toast.error('Please select a plan');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/agency/${agencyId || 'default_agency'}/whop/checkout-link-v2`,
        {
          plan_mapping_id: selectedMapping,
          contact_id: contactId,
          email: contactEmail,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGeneratedLink(res.data);
      toast.success('Checkout link generated!');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate checkout link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Link2 className="w-5 h-5 text-emerald-500" />
            Send Whop Checkout Link
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Generate a checkout link with tracking metadata for this contact.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!generatedLink ? (
            <>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Select Plan</label>
                <Select value={selectedMapping} onValueChange={setSelectedMapping}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="select-plan-for-checkout">
                    <SelectValue placeholder="Choose a plan..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {mappings.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        <div className="flex items-center gap-2">
                          <span>{m.mapping_name}</span>
                          {m.whop_price && (
                            <span className="text-zinc-500 text-xs">
                              ${(m.whop_price / 100).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {contactEmail && (
                <div className="p-3 bg-zinc-800/50 rounded-lg text-sm">
                  <div className="flex justify-between text-zinc-400">
                    <span>Contact Email:</span>
                    <span className="text-white">{contactEmail}</span>
                  </div>
                </div>
              )}

              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-blue-300">
                    This link will include tracking metadata (agency_id, contact_id, mapping_id) for accurate routing.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-300 font-medium">Link Generated!</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Plan:</span>
                    <span className="text-white">{generatedLink.plan_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Access Level:</span>
                    <span className="text-white">{generatedLink.access_level?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Self Repair:</span>
                    <span className={generatedLink.self_repair_enabled ? 'text-emerald-400' : 'text-red-400'}>
                      {generatedLink.self_repair_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Checkout URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={generatedLink.purchase_url}
                    className="flex-1 bg-zinc-800 border border-zinc-700 text-white px-3 py-2 rounded-lg text-sm font-mono overflow-hidden text-ellipsis"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(generatedLink.purchase_url)}
                    className="border-zinc-600"
                    data-testid="copy-checkout-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-zinc-600"
                  onClick={() => window.open(`mailto:${contactEmail}?subject=Your%20Checkout%20Link&body=${encodeURIComponent(generatedLink.purchase_url)}`, '_blank')}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send via Email
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-zinc-600"
                  onClick={() => window.open(generatedLink.purchase_url, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Link
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {!generatedLink ? (
            <>
              <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={loading || !selectedMapping}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="generate-checkout-link-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                Generate Link
              </Button>
            </>
          ) : (
            <Button onClick={onClose} className="bg-emerald-500 hover:bg-emerald-600 text-white">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN WHOP STATUS CARD ============
const WhopStatusCard = ({ 
  contactId, 
  contactEmail,
  agencyId,
  token,
  // Optional: pass pre-loaded data to avoid extra API calls
  membershipData = null,
  onUpdate = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState(membershipData);
  const [invite, setInvite] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);

  useEffect(() => {
    if (contactId && token && !membershipData) {
      fetchMembershipData();
    } else if (membershipData) {
      setMembership(membershipData);
      setLoading(false);
    }
  }, [contactId, token, membershipData]);

  const fetchMembershipData = async () => {
    setLoading(true);
    try {
      // Fetch membership by contact_id
      const res = await axios.get(
        `${API}/api/agency/${agencyId || 'default_agency'}/whop/memberships?contact_id=${contactId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.items && res.data.items.length > 0) {
        setMembership(res.data.items[0]);
        
        // Fetch invite if membership has invite_id
        if (res.data.items[0].invite_id) {
          try {
            const inviteRes = await axios.get(
              `${API}/api/agency/${agencyId || 'default_agency'}/whop/invites/${res.data.items[0].invite_id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setInvite(inviteRes.data);
          } catch (err) {
            // Invite fetch failed, non-critical
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch membership data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncStatus = async () => {
    if (!contactId) return;
    
    setSyncing(true);
    try {
      // Call the new sync-status endpoint that uses Whop normalizers
      const res = await axios.post(
        `${API}/api/agency/${agencyId || 'default_agency'}/whop/contacts/${contactId}/sync-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success('Whop status synced successfully');
        
        // If membership was updated, use the returned data
        if (res.data.membership) {
          setMembership(prev => ({
            ...prev,
            whop_product_name: res.data.membership.product_name,
            whop_plan_name: res.data.membership.plan_name,
            status: res.data.membership.status,
            valid: res.data.membership.valid,
          }));
        } else {
          // Refetch to get latest data
          fetchMembershipData();
        }
        
        if (onUpdate) onUpdate();
      } else {
        toast.error(res.data.errors?.[0] || 'Sync completed with issues');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync status');
    } finally {
      setSyncing(false);
    }
  };

  const handleResendInvite = async () => {
    if (!invite?.invite_id) return;
    
    try {
      await axios.post(
        `${API}/api/agency/${agencyId || 'default_agency'}/whop/invites/${invite.invite_id}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invite resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    }
  };

  // Helper to get display name for product/plan
  const getProductPlanDisplay = () => {
    const productName = membership?.whop_product_name;
    const planName = membership?.whop_plan_name;
    const productId = membership?.whop_product_id;
    const planId = membership?.whop_plan_id;
    
    // Priority: Names first, then IDs as fallback
    if (productName && planName) {
      return { product: productName, plan: planName };
    } else if (productName) {
      return { product: productName, plan: planId ? `Plan: ${planId.slice(0, 12)}...` : 'N/A' };
    } else if (planName) {
      return { product: productId ? `Product: ${productId.slice(0, 12)}...` : 'N/A', plan: planName };
    } else {
      // Show IDs but truncated
      return {
        product: productId ? `${productId.slice(0, 15)}...` : 'N/A',
        plan: planId ? `${planId.slice(0, 15)}...` : 'N/A',
      };
    }
  };

  // Determine the actual status to show
  // Important: Don't show "invalid" for active memberships
  const getDisplayStatus = () => {
    if (!membership) return 'unknown';
    
    const status = (membership.status || '').toLowerCase();
    const valid = membership.valid;
    
    // If valid flag is true, show active regardless of status string
    if (valid === true) return 'active';
    
    // Map status strings
    const statusMap = {
      'active': 'active',
      'trialing': 'trialing',
      'past_due': 'past_due',
      'canceled': 'canceled',
      'cancelled': 'canceled',
      'expired': 'expired',
      'paused': 'paused',
      'incomplete': 'pending',
      'valid': 'active',
      'invalid': membership.lock_reason ? 'locked' : 'expired',
    };
    
    return statusMap[status] || status || 'unknown';
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-4 flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const displayStatus = getDisplayStatus();
  const { product, plan } = membership ? getProductPlanDisplay() : { product: 'N/A', plan: 'N/A' };

  return (
    <>
      <Card className="bg-zinc-900/50 border-zinc-800" data-testid="whop-status-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <ShoppingCart className="w-4 h-4 text-emerald-500" />
              Whop Membership
            </CardTitle>
            {membership && (
              <WhopStatusBadge status={displayStatus} />
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {!membership ? (
            <div className="text-center py-4">
              <ShoppingCart className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">No Whop membership found</p>
              <Button
                size="sm"
                className="mt-3 bg-emerald-500 hover:bg-emerald-600"
                onClick={() => setCheckoutModalOpen(true)}
                data-testid="send-checkout-link-btn"
              >
                <Link2 className="w-4 h-4 mr-2" />
                Send Checkout Link
              </Button>
            </div>
          ) : (
            <>
              {/* Status Info - Show Names First, IDs Second */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="space-y-1">
                  <p className="text-zinc-500 text-xs">Product</p>
                  <p className="text-zinc-300" title={membership.whop_product_id}>
                    {product}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-zinc-500 text-xs">Plan</p>
                  <p className="text-zinc-300" title={membership.whop_plan_id}>
                    {plan}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-zinc-500 text-xs">Access Level</p>
                  <p className="text-zinc-300">{membership.access_level?.replace(/_/g, ' ') || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-zinc-500 text-xs">Self Repair Access</p>
                  <SelfRepairAccessBadge access={
                    membership.is_portal_access_granted ? 'active' :
                    membership.self_repair_enabled ? 'pending' : 'locked'
                  } />
                </div>
              </div>

              {/* Payment Info */}
              {membership.current_period_end && (
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Calendar className="w-4 h-4" />
                      <span>Current Period End</span>
                    </div>
                    <span className="text-white">
                      {new Date(membership.current_period_end).toLocaleDateString()}
                    </span>
                  </div>
                  {membership.last_payment_at && (
                    <div className="flex items-center justify-between text-sm mt-2">
                      <div className="flex items-center gap-2 text-zinc-400">
                        <DollarSign className="w-4 h-4" />
                        <span>Last Payment</span>
                      </div>
                      <span className="text-white">
                        {new Date(membership.last_payment_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Invite Status */}
              {invite && (
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-zinc-400" />
                      <span className="text-zinc-400 text-sm">Signup Invite</span>
                    </div>
                    <InviteStatusBadge status={invite.status} />
                  </div>
                  {invite.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-2 border-zinc-700"
                      onClick={handleResendInvite}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Resend Invite
                    </Button>
                  )}
                </div>
              )}

              {/* Lock Reason */}
              {membership.lock_reason && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Lock className="w-4 h-4 text-red-400 mt-0.5" />
                    <div>
                      <p className="text-red-300 text-sm font-medium">Access Locked</p>
                      <p className="text-red-400/80 text-xs mt-1">{membership.lock_reason}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Mapping Status */}
              {membership.mapping_status === 'unmapped' && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-amber-300 text-sm font-medium">Unmapped Product</p>
                      <p className="text-amber-400/80 text-xs mt-1">
                        This Whop product is not mapped to a FundElite access level.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-zinc-700"
                  onClick={handleSyncStatus}
                  disabled={syncing}
                >
                  {syncing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Sync Status
                </Button>
                <Button
                  size="sm"
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => setCheckoutModalOpen(true)}
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  New Link
                </Button>
              </div>

              {/* User Link */}
              {membership.user_id && (
                <div className="pt-2 border-t border-zinc-800">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full text-zinc-400 hover:text-white"
                    onClick={() => window.open(`/platform/users/${membership.user_id}`, '_blank')}
                  >
                    <User className="w-4 h-4 mr-2" />
                    View Portal User Account
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Checkout Link Modal */}
      <SendCheckoutLinkModal
        isOpen={checkoutModalOpen}
        onClose={() => setCheckoutModalOpen(false)}
        contactId={contactId}
        contactEmail={contactEmail || membership?.email}
        agencyId={agencyId}
        token={token}
        onSuccess={fetchMembershipData}
      />
    </>
  );
};

export default WhopStatusCard;
